import { ToastAction, type ToastActionElement } from '@/components/ui/toast';

/**
 * Shape passed to `toast()` — mirrors the fields the builder uses. Kept local
 * so this helper has no dependency on the toast hook's internals.
 */
export interface CreditAwareToastOptions {
  title: string;
  description: string;
  variant: 'destructive';
  duration: number;
  action?: ToastActionElement;
}

export interface CreditAwareErrorToastInput {
  /** Human-readable error message from the failed request. */
  message: string;
  /** Error code from the API (e.g. `'INSUFFICIENT_CREDITS'`), when present. */
  code?: string;
  /** Toast title for non-credit errors (e.g. `'Error'`, `'Publish Failed'`). */
  fallbackTitle: string;
  /** Invoked by the "Buy more credits" CTA — typically `navigate('/credits')`. */
  onBuyCredits: () => void;
}

/**
 * Build the toast options for a failed builder request, branching on whether
 * the failure is a credit shortfall.
 *
 * A credit shortfall is user-actionable, not an internal fault: instead of a
 * dead-end error that drops the user out of whatever they were doing (building
 * OR publishing), surface a friendly prompt with a one-click path to top up.
 * Shared by the streaming `onError` handler and the publish handler so both
 * flows behave identically.
 */
export function creditAwareErrorToast(input: CreditAwareErrorToastInput): CreditAwareToastOptions {
  if (input.code === 'INSUFFICIENT_CREDITS') {
    return {
      title: 'Out of credits',
      description: input.message,
      variant: 'destructive',
      duration: 15000,
      action: (
        <ToastAction altText="Buy more credits" onClick={input.onBuyCredits}>
          Buy more credits
        </ToastAction>
      ),
    };
  }
  return {
    title: input.fallbackTitle,
    description: input.message,
    variant: 'destructive',
    duration: 8000,
  };
}
